/**
 * Parse hub.yaml + agents/<slug>.yaml + agents/*.md into HubAsCodePayload.
 * Falls back to wayai.yaml for backward compatibility.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import * as yaml from 'js-yaml';
import { slugify, resolveHubYamlPath } from './utils.js';
import { scanResourceFiles } from './resource-files.js';
import type { HubAsCodePayload, HubAsCodeResource, HubAsCodeEval, HubAsCodeJourney } from './types.js';

/** Tag an Error as user-facing so the CLI prints just the message (no stack). */
function expected(message: string): Error {
  const err = new Error(message);
  (err as Error & { isExpected: boolean }).isExpected = true;
  return err;
}

interface WayaiYaml {
  version?: number;
  hub_id: string;
  hub_environment: string;
  preview_label?: string;
  hub: Record<string, unknown>;
  agents?: Array<Record<string, unknown>>;
  resources?: Array<Record<string, unknown>>;
  states?: Array<Record<string, unknown>>;
  teams?: Array<Record<string, unknown>>;
  connections?: Array<{
    name: string; type: string; service?: string; credential?: string; access_token_credential?: string;
    no_auth?: boolean; base_url?: string; headers?: Record<string, string>; settings?: Record<string, unknown>;
    account_sid?: string; enabled?: boolean; authentication_type?: string; sync_credentials_to_production?: boolean;
  }>;
  outbound_contacts?: Array<Record<string, unknown>>;
  outbound_lists?: Array<Record<string, unknown>>;
  outbound_schedules?: Array<Record<string, unknown>>;
}

/**
 * Parse a hub folder into a HubAsCodePayload.
 * Reads hub.yaml (or wayai.yaml for backward compat) and resolves agents from
 * agents/<slug>.yaml files (or inline agents array for old format).
 * Resolves agent instructions from companion .md files.
 */
export interface ParseHubFolderOptions {
  /** When true, allow hub_id and hub_environment to be absent (for new hubs not yet created on platform). */
  allowNewHub?: boolean;
}

export function parseHubFolder(hubFolder: string, opts?: ParseHubFolderOptions): HubAsCodePayload {
  const yamlPath = resolveHubYamlPath(hubFolder);
  if (!yamlPath) {
    const err = new Error(`hub.yaml not found in ${hubFolder}`);
    (err as Error & { isExpected: boolean }).isExpected = true;
    throw err;
  }

  const yamlContent = fs.readFileSync(yamlPath, 'utf-8');
  let config: WayaiYaml;
  try {
    config = yaml.load(yamlContent) as WayaiYaml;
  } catch (err) {
    if (err instanceof yaml.YAMLException) {
      const userError = new Error(`Invalid YAML in ${yamlPath}: ${err.message}`);
      (userError as Error & { isExpected: boolean }).isExpected = true;
      throw userError;
    }
    throw err;
  }

  if (!config || typeof config !== 'object') {
    const invalidError = new Error(`Invalid YAML in ${yamlPath}`);
    (invalidError as Error & { isExpected: boolean }).isExpected = true;
    throw invalidError;
  }

  if (!opts?.allowNewHub) {
    if (!config.hub_id) {
      const err = new Error(`Missing hub_id in ${yamlPath}`);
      (err as Error & { isExpected: boolean }).isExpected = true;
      throw err;
    }

    if (!config.hub_environment) {
      const err = new Error(`Missing hub_environment in ${yamlPath}`);
      (err as Error & { isExpected: boolean }).isExpected = true;
      throw err;
    }
  }

  // Resolve agents: try per-agent YAML files first, fall back to inline agents
  const agentYamlFiles = scanAgentYamlFiles(hubFolder);
  const rawAgents = agentYamlFiles.length > 0 ? agentYamlFiles : config.agents;

  // Resolve agent instructions from .md files
  const agents = rawAgents?.map((agent) => {
    const resolved = { ...agent };

    if (typeof resolved.instructions === 'string' && resolved.instructions.endsWith('.md')) {
      // Explicit path: "agents/pilot.md" or "pilot.md" (legacy)
      const instrValue = resolved.instructions as string;
      const instructionsPath = instrValue.startsWith('agents/')
        ? path.join(hubFolder, instrValue)
        : path.join(hubFolder, 'agents', instrValue);

      if (fs.existsSync(instructionsPath)) {
        resolved.instructions = fs.readFileSync(instructionsPath, 'utf-8');
      } else {
        const err = new Error(
          `Agent instructions file not found: ${instructionsPath} (referenced by agent "${agent.name}")`
        );
        (err as Error & { isExpected: boolean }).isExpected = true;
        throw err;
      }
    } else if (resolved.instructions === undefined && typeof agent.name === 'string') {
      // Convention-based: derive path from agent name slug
      const conventionPath = path.join(hubFolder, 'agents', `${slugify(agent.name)}.md`);
      if (fs.existsSync(conventionPath)) {
        resolved.instructions = fs.readFileSync(conventionPath, 'utf-8');
      }
    }

    return resolved;
  });

  // Resolve resource files from disk
  const resources = parseResources(hubFolder, (config.resources || []) as Array<Record<string, unknown>>);

  const payload: HubAsCodePayload = {
    version: config.version || 1,
    hub_id: config.hub_id || '',
    hub_environment: config.hub_environment || 'preview',
    hub: config.hub as HubAsCodePayload['hub'],
  };

  // Read-only metadata, server-owned: set by the backend's replicate-preview /
  // hub-update PATCH (contracts/setup/hubs.ts), never authored here. Only used to
  // disambiguate sibling preview-hub folders via the `--<label>` slug suffix
  // (see getHubFolderSlug in workspace.ts). Present only on preview hubs.
  if (config.preview_label) {
    payload.preview_label = config.preview_label;
  }

  if (agents && agents.length > 0) {
    payload.agents = agents as unknown as HubAsCodePayload['agents'];
  }

  if (resources.length > 0) {
    payload.resources = resources;
  }

  if (config.states && config.states.length > 0) {
    payload.states = config.states as unknown as HubAsCodePayload['states'];
  }

  if (config.teams && config.teams.length > 0) {
    payload.teams = config.teams as unknown as HubAsCodePayload['teams'];
  }

  // Emit `connections` whenever it's an array — INCLUDING an empty `[]` — so a
  // hub.yaml that removes its last connection block (present-but-empty) deletes
  // it on push. Unlike other entities (which `|| []` on the diff side, treating
  // absent == empty), the connection delete guard distinguishes an ABSENT key
  // (no-op) from a present `[]` (delete all unreferenced); the parser must
  // preserve that. A valueless `connections:` (js-yaml null) is intentionally
  // omitted → treated as absent (safe no-op).
  if (Array.isArray(config.connections)) {
    payload.connections = config.connections;
  }

  if (config.outbound_contacts && config.outbound_contacts.length > 0) {
    payload.outbound_contacts = config.outbound_contacts as unknown as HubAsCodePayload['outbound_contacts'];
  }

  if (config.outbound_lists && config.outbound_lists.length > 0) {
    payload.outbound_lists = config.outbound_lists as unknown as HubAsCodePayload['outbound_lists'];
  }

  if (config.outbound_schedules && config.outbound_schedules.length > 0) {
    payload.outbound_schedules = config.outbound_schedules as unknown as HubAsCodePayload['outbound_schedules'];
  }

  const evals = scanEvalYamlFiles(hubFolder);
  if (evals.length > 0) {
    payload.evals = evals;
  }

  const journeys = scanJourneyYamlFiles(hubFolder);
  if (journeys.length > 0) {
    payload.journeys = journeys;
  }

  return payload;
}

// Scan evals/<slug>.yaml (root, path=null) and evals/<set>/<slug>.yaml (one level deep only — flat-set model).
function scanEvalYamlFiles(hubFolder: string): HubAsCodeEval[] {
  const evalsDir = path.join(hubFolder, 'evals');
  if (!fs.existsSync(evalsDir)) return [];

  const evals: HubAsCodeEval[] = [];
  const seen = new Set<string>(); // composite key "<name>|<path>" — must be unique

  const topEntries = fs.readdirSync(evalsDir, { withFileTypes: true })
    .sort((a, b) => a.name.localeCompare(b.name));

  for (const entry of topEntries) {
    if (entry.isFile() && entry.name.endsWith('.yaml')) {
      // Root-level scenario (no scenario set)
      collectEval(evals, seen, path.join(evalsDir, entry.name), `evals/${entry.name}`, null);
    } else if (entry.isDirectory()) {
      // Scenario-set subfolder (one level deep only)
      const setName = entry.name;
      const setDir = path.join(evalsDir, setName);
      const setEntries = fs.readdirSync(setDir, { withFileTypes: true })
        .sort((a, b) => a.name.localeCompare(b.name));
      for (const sub of setEntries) {
        if (sub.isDirectory()) {
          throw expected(
            `Nested scenario sets are not supported: ${path.relative(hubFolder, path.join(setDir, sub.name))}. ` +
              `Move scenarios up to evals/${setName}/.`,
          );
        }
        if (sub.isFile() && sub.name.endsWith('.yaml')) {
          collectEval(evals, seen, path.join(setDir, sub.name), `evals/${setName}/${sub.name}`, setName);
        }
      }
    }
  }

  return evals;
}

function collectEval(
  evals: HubAsCodeEval[],
  seen: Set<string>,
  filePath: string,
  relPath: string,
  setName: string | null,
): void {
  const evalEntry = parseEvalYaml(filePath, relPath, setName);
  if (!evalEntry) return;
  const key = `${evalEntry.name}|${setName ?? ''}`;
  if (seen.has(key)) {
    const where = setName ? `scenario set "${setName}"` : 'root';
    throw expected(
      `Duplicate eval at ${where}: "${evalEntry.name}" (in ${path.basename(filePath)}). ` +
        `Each (name, scenario set) pair must be unique.`,
    );
  }
  seen.add(key);
  evals.push(evalEntry);
}

// Returns null on soft failures (empty/malformed file, warning logged); throws expected() on hard validation.
function parseEvalYaml(filePath: string, relPath: string, setName: string | null): HubAsCodeEval | null {
  const content = fs.readFileSync(filePath, 'utf-8');
  let raw: unknown;
  try {
    raw = yaml.load(content);
  } catch (err) {
    if (err instanceof yaml.YAMLException) {
      throw expected(`Invalid YAML in ${relPath}: ${err.message}`);
    }
    throw err;
  }

  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    console.warn(`  Warning: skipping ${relPath} (not a YAML object)`);
    return null;
  }

  const data = raw as Record<string, unknown>;
  const fileSlug = path.basename(filePath, '.yaml');
  const evalName = typeof data.name === 'string' && data.name.trim().length > 0 ? data.name : fileSlug;

  // Required fields: agent, input, expected
  if (typeof data.agent !== 'string' || data.agent.trim().length === 0) {
    throw expected(`Eval "${evalName}" in ${relPath}: missing required field "agent" (string).`);
  }
  if (!data.input || typeof data.input !== 'object' || Array.isArray(data.input)) {
    throw expected(`Eval "${evalName}" in ${relPath}: missing required field "input" (object).`);
  }
  if (!data.expected || typeof data.expected !== 'object' || Array.isArray(data.expected)) {
    throw expected(`Eval "${evalName}" in ${relPath}: missing required field "expected" (object).`);
  }

  const evalEntry: HubAsCodeEval = {
    name: evalName,
    agent: data.agent,
    path: setName,
    input: data.input as HubAsCodeEval['input'],
    expected: data.expected as HubAsCodeEval['expected'],
  };

  if (typeof data.id === 'string') evalEntry.id = data.id;
  if (typeof data.agent_id === 'string') evalEntry.agent_id = data.agent_id;
  if (typeof data.runs === 'number') evalEntry.runs = data.runs;
  if (data.enabled === false) evalEntry.enabled = false;
  if (Array.isArray(data.history)) {
    evalEntry.history = data.history as HubAsCodeEval['history'];
  }
  if (typeof data.evaluator_instructions === 'string') {
    evalEntry.evaluator_instructions = data.evaluator_instructions;
  }
  const variables = parseVariablesMap(data.variables, `Eval "${evalName}" in ${relPath}`);
  if (variables) evalEntry.variables = variables;

  return evalEntry;
}

/**
 * Parse an optional `variables:` map-of-arrays (gh #3007), shared by the eval and
 * journey parsers. Returns the map when present and well-formed, `undefined` when
 * the key is absent (or not an object), and throws with the caller-supplied
 * `label` when it's an object with a non-array value — so a malformed author edit
 * surfaces an error rather than silently dropping the field.
 */
function parseVariablesMap(raw: unknown, label: string): Record<string, unknown[]> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const vars = raw as Record<string, unknown>;
  // An empty map is treated as absent — parity with the server's `hasEvalVariables`
  // and the emitter (which omits empty), so a hand-authored `variables: {}` doesn't
  // produce a phantom `{}`-vs-null diff that oscillates on every push/pull.
  if (Object.keys(vars).length === 0) return undefined;
  if (!Object.values(vars).every((v) => Array.isArray(v))) {
    throw expected(`${label}: "variables" must be a map of arrays (variables.<name>: [ ... ]).`);
  }
  return vars as Record<string, unknown[]>;
}

// Scan journeys/<slug>.yaml — a FLAT folder (one file per journey). Unlike evals
// there are no scenario-set subfolders: a journey owns its own managed set, which
// is NOT authored. Returns journeys sorted alphabetically by filename.
//
// NOTE on step ids: the backend mints stable `step_id`s on concluding assistant
// turns at create time (`ensureStepIds`) and keys per-step `step_config` overrides
// + derived evals by them. A hand-authored journey pushed WITHOUT step_ids gets
// server-minted ids in the DB but not on disk — so run `wayai pull` after creating
// or first-pushing a journey to sync those ids back (same "pull to sync
// server-assigned ids" discipline as entity `id`s). Re-storing a journey whose
// on-disk transcript lacks the server's step_ids re-mints them, severing
// derived-eval history.
function scanJourneyYamlFiles(hubFolder: string): HubAsCodeJourney[] {
  const journeysDir = path.join(hubFolder, 'journeys');
  if (!fs.existsSync(journeysDir)) return [];

  const journeys: HubAsCodeJourney[] = [];
  const seen = new Set<string>(); // journey name — must be unique

  const entries = fs.readdirSync(journeysDir, { withFileTypes: true })
    .sort((a, b) => a.name.localeCompare(b.name));

  for (const entry of entries) {
    if (entry.isDirectory()) {
      throw expected(
        `Subfolders are not supported under journeys/: ${path.relative(hubFolder, path.join(journeysDir, entry.name))}. ` +
          `A journey owns its own managed scenario set — put each journey in a single journeys/<slug>.yaml file.`,
      );
    }
    if (!entry.isFile() || !entry.name.endsWith('.yaml')) continue;

    const journeyEntry = parseJourneyYaml(path.join(journeysDir, entry.name), `journeys/${entry.name}`);
    if (!journeyEntry) continue;
    if (seen.has(journeyEntry.name)) {
      throw expected(
        `Duplicate journey: "${journeyEntry.name}" (in ${entry.name}). Each journey name must be unique.`,
      );
    }
    seen.add(journeyEntry.name);
    journeys.push(journeyEntry);
  }

  return journeys;
}

// Returns null on soft failures (empty/malformed file, warning logged); throws expected() on hard validation.
function parseJourneyYaml(filePath: string, relPath: string): HubAsCodeJourney | null {
  const content = fs.readFileSync(filePath, 'utf-8');
  let raw: unknown;
  try {
    raw = yaml.load(content);
  } catch (err) {
    if (err instanceof yaml.YAMLException) {
      throw expected(`Invalid YAML in ${relPath}: ${err.message}`);
    }
    throw err;
  }

  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    console.warn(`  Warning: skipping ${relPath} (not a YAML object)`);
    return null;
  }

  const data = raw as Record<string, unknown>;
  const fileSlug = path.basename(filePath, '.yaml');
  const journeyName = typeof data.name === 'string' && data.name.trim().length > 0 ? data.name : fileSlug;

  // Required field: agent. transcript may be empty/absent (the backend allows a
  // 0-step journey to create — match eval leniency).
  if (typeof data.agent !== 'string' || data.agent.trim().length === 0) {
    throw expected(`Journey "${journeyName}" in ${relPath}: missing required field "agent" (string).`);
  }
  if (data.transcript !== undefined && !Array.isArray(data.transcript)) {
    throw expected(`Journey "${journeyName}" in ${relPath}: "transcript" must be an array.`);
  }
  if (data.step_config !== undefined && (typeof data.step_config !== 'object' || Array.isArray(data.step_config) || data.step_config === null)) {
    throw expected(`Journey "${journeyName}" in ${relPath}: "step_config" must be an object keyed by step_id.`);
  }

  const journeyEntry: HubAsCodeJourney = {
    name: journeyName,
    agent: data.agent,
  };

  if (typeof data.id === 'string') journeyEntry.id = data.id;
  if (typeof data.agent_id === 'string') journeyEntry.agent_id = data.agent_id;
  if (data.enabled === false) journeyEntry.enabled = false;
  if (typeof data.evaluator_instructions === 'string') {
    journeyEntry.evaluator_instructions = data.evaluator_instructions;
  }
  if (Array.isArray(data.transcript)) {
    journeyEntry.transcript = data.transcript as HubAsCodeJourney['transcript'];
  }
  if (data.step_config && typeof data.step_config === 'object' && !Array.isArray(data.step_config)) {
    journeyEntry.step_config = data.step_config as HubAsCodeJourney['step_config'];
  }
  // Per-run variables (gh #3007): a map of arrays, propagated to every derived step.
  const variables = parseVariablesMap(data.variables, `Journey "${journeyName}" in ${relPath}`);
  if (variables) journeyEntry.variables = variables;

  return journeyEntry;
}

/**
 * Scan agents/ directory for per-agent YAML config files.
 * Returns agent configs sorted alphabetically by filename.
 * Resolves instructions from companion .md files.
 */
function scanAgentYamlFiles(hubFolder: string): Array<Record<string, unknown>> {
  const agentsDir = path.join(hubFolder, 'agents');
  if (!fs.existsSync(agentsDir)) return [];

  const yamlFiles = fs.readdirSync(agentsDir)
    .filter((f) => f.endsWith('.yaml'))
    .sort();

  if (yamlFiles.length === 0) return [];

  const agents: Array<Record<string, unknown>> = [];
  for (const file of yamlFiles) {
    const filePath = path.join(agentsDir, file);
    const content = fs.readFileSync(filePath, 'utf-8');
    let agent: Record<string, unknown>;
    try {
      agent = yaml.load(content) as Record<string, unknown>;
    } catch (err) {
      if (err instanceof yaml.YAMLException) {
        const userError = new Error(`Invalid YAML in agents/${file}: ${err.message}`);
        (userError as Error & { isExpected: boolean }).isExpected = true;
        throw userError;
      }
      throw err;
    }
    if (!agent || typeof agent !== 'object') continue;
    if (!agent.name) {
      console.warn(`  Warning: skipping ${file} (missing 'name' field)`);
      continue;
    }
    agents.push(agent);
  }

  return agents;
}

/**
 * Scan resource directories and populate file entries with content and hashes.
 */
function parseResources(
  hubFolder: string,
  configResources: Array<Record<string, unknown>>
): HubAsCodeResource[] {
  const resourcesDir = path.join(hubFolder, 'resources');
  const results: HubAsCodeResource[] = [];

  for (const res of configResources) {
    const resource: HubAsCodeResource = {
      name: res.name as string,
    };
    if (res.id) resource.id = res.id as string;
    if (res.type) resource.type = res.type as string;
    if (res.description) resource.description = res.description as string;
    if (res.enabled === false) resource.enabled = false;
    if (res.user_browsable) resource.user_browsable = res.user_browsable as boolean;
    if (res.skill_name) resource.skill_name = res.skill_name as string;

    // Scan filesystem for files
    const resSlug = slugify(resource.name);
    const resDir = path.join(resourcesDir, resSlug);

    if (fs.existsSync(resDir)) {
      const files = scanResourceFiles(resDir, '');
      if (files.length > 0) {
        resource.files = files;
      }
    }

    results.push(resource);
  }

  return results;
}
